/**
 * Mobile WebView bridge.
 *
 * Detektuje da li web radi unutar SUTON Radio native WebView wrapper-a (Expo
 * react-native-webview injektira `window.ReactNativeWebView.postMessage`).
 *
 * U mobile mod-u, sva audio reprodukcija delegira se native sloju (preko
 * postMessage protokola). UI sloj nastavlja da koristi useAudioStore za
 * prikaz state-a (currentStation, isPlaying, currentTrack itd.).
 *
 * U browseru (PWA), sve `mobileBridge.*` pozive su NoOp i `isMobileApp()`
 * vraća false — postojeći Web Audio API path radi kao i prije.
 */

interface ReactNativeWebViewBridge {
  postMessage: (message: string) => void;
}

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebViewBridge;
    /** Injected by native WebView (Build 10+). */
    __SUTON_NATIVE__?: { platform?: 'ios' | 'android'; version?: string };
  }
}

export type BridgeMessageType =
  | 'PLAY'
  | 'PAUSE'
  | 'STOP'
  | 'VOLUME'
  | 'TRACK_UPDATE'
  | 'EQUALIZER'
  | 'LOUDNESS'
  | 'EQ_DIAG_REQUEST'
  | 'AIRPLAY_PICKER';

export interface BridgePlayPayload {
  stationId: string;
  stationName: string;
  streamUrl: string;
  artwork?: string;
  color?: string;
  genre?: string;
  currentTrack?: {
    artist: string;
    title: string;
    artwork?: string;
  };
}

export interface BridgeVolumePayload {
  value: number;
}

export interface BridgeTrackUpdatePayload {
  artist: string;
  title: string;
  artwork?: string;
}

export interface BridgeEqualizerPayload {
  /** Array of 5 band gains in dB, range -12 to +12 */
  bands: number[];
}

export interface BridgeLoudnessPayload {
  /** Master loudness boost in dB, 0 to 14 */
  gain: number;
}

export type BridgeMessage =
  | { type: 'PLAY'; payload: BridgePlayPayload }
  | { type: 'PAUSE' }
  | { type: 'STOP' }
  | { type: 'VOLUME'; payload: BridgeVolumePayload }
  | { type: 'TRACK_UPDATE'; payload: BridgeTrackUpdatePayload }
  | { type: 'EQUALIZER'; payload: BridgeEqualizerPayload }
  | { type: 'LOUDNESS'; payload: BridgeLoudnessPayload }
  | { type: 'EQ_DIAG_REQUEST' }
  | { type: 'AIRPLAY_PICKER' };

/**
 * Provjerava da li smo unutar mobile WebView-a.
 *
 * Provjera je stroga (postoji `postMessage` funkcija) da spriječi false-positive
 * u browser ekstenzijama ili kompatibilnim env-ima.
 */
export function isMobileApp(): boolean {
  if (typeof window === 'undefined') return false;
  const rnwv = window.ReactNativeWebView;
  return !!rnwv && typeof rnwv.postMessage === 'function';
}

/** Native platform inside WebView (Build 10+ inject). Fallback: UA. */
export function getNativePlatform(): 'ios' | 'android' | null {
  if (!isMobileApp()) return null;
  const injected = window.__SUTON_NATIVE__?.platform;
  if (injected === 'ios' || injected === 'android') return injected;
  if (typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return 'ios';
  }
  if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)) {
    return 'android';
  }
  return null;
}

function send(message: BridgeMessage): void {
  if (!isMobileApp()) return;
  try {
    window.ReactNativeWebView!.postMessage(JSON.stringify(message));
  } catch (err) {
    console.warn('[mobileBridge] postMessage failed:', err);
  }
}

export const mobileBridge = {
  play(payload: BridgePlayPayload): void {
    send({ type: 'PLAY', payload });
  },
  pause(): void {
    send({ type: 'PAUSE' });
  },
  stop(): void {
    send({ type: 'STOP' });
  },
  volume(value: number): void {
    const clamped = Math.max(0, Math.min(1, value));
    send({ type: 'VOLUME', payload: { value: clamped } });
  },
  trackUpdate(payload: BridgeTrackUpdatePayload): void {
    send({ type: 'TRACK_UPDATE', payload });
  },
  equalizer(bands: number[]): void {
    // 5-band gain values in dB, native sloj konvertuje u millibel za Android Equalizer API
    send({ type: 'EQUALIZER', payload: { bands } });
  },
  loudness(gain: number): void {
    // Master loudness boost in dB (0..14), Android LoudnessEnhancer
    send({ type: 'LOUDNESS', payload: { gain } });
  },
  /**
   * R6.4: traži od native sloja TRENUTNI reflection status (zašto attach
   * možda fail-uje). Native odgovara preko 'native-eq-result' eventa sa
   * `isDiagOnly: true` payload-om. Mora R6.4+ APK; stari APK ignoriše.
   */
  requestEqDiag(): void {
    send({ type: 'EQ_DIAG_REQUEST' });
  },
  /**
   * 2b: otvori native AirPlay / audio route picker (iOS).
   * Build 9 ignoriše nepoznati tip; Build 10+ prikazuje AVRoutePickerView.
   */
  airplayPicker(): void {
    send({ type: 'AIRPLAY_PICKER' });
  },
};
